import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Star, CheckCircle2 } from 'lucide-react';
const bundles = [{
  label: '1 Unit',
  desc: 'For apartments & single rooms',
  price: '$69.95',
  crossed: '$119.95',
  saveBadge: 'SAVE 42%',
  badgeColor: 'text-blue-600 bg-blue-50',
  units: 1,
  highlighted: false,
  topBadge: null,
  checkoutUrl: 'https://www.pryvex.store/cart/53569915715921:1'
}, {
  label: '2-Pack',
  desc: 'Full protection for 1-story homes',
  price: '$119.95',
  crossed: '$239.90',
  saveBadge: 'SAVE 50%',
  badgeColor: 'text-blue-600 bg-blue-50',
  units: 2,
  highlighted: false,
  topBadge: null,
  checkoutUrl: 'https://www.pryvex.store/cart/53569914798417:1'
}, {
  label: '3-Pack',
  desc: 'Complete coverage for 2-3 story homes',
  price: '$149.95',
  crossed: '$359.85',
  saveBadge: 'SAVE 58%',
  badgeColor: 'text-white bg-accent-orange',
  units: 3,
  highlighted: true,
  topBadge: '★ MOST POPULAR ★',
  checkoutUrl: 'https://www.pryvex.store/cart/53569911095633:1'
}, {
  label: '4-Pack',
  desc: 'Maximum protection for larger homes',
  price: '$189.95',
  crossed: '$479.80',
  saveBadge: 'SAVE 60%',
  badgeColor: 'text-white bg-accent-green',
  units: 4,
  highlighted: false,
  topBadge: 'BEST VALUE',
  checkoutUrl: 'https://www.pryvex.store/cart/53570206892369:1'
}];
const trustAccordion = [{
  title: 'How does AirGuard work?',
  content: 'AirGuard Pro uses a precision electrochemical sensor to continuously monitor CO levels in real time. It displays the exact PPM reading on a 2-inch color screen and triggers a 4-stage visual + audible alert system starting at just 30 PPM — hours before traditional detectors react.'
}, {
  title: 'Shipping & Delivery',
  content: "All orders ship free worldwide within 24 hours of purchase. US orders arrive in 3-5 business days. International orders arrive in 7-14 business days. You'll receive a tracking number via email as soon as your order ships."
}, {
  title: '3-Year Performance Guarantee',
  content: 'Every AirGuard Pro is backed by a 3-year warranty covering any manufacturing defect or sensor issue. If anything goes wrong, we replace it — no questions asked. The sensor itself is rated for 10 years of continuous use.'
}];
function ProductThumbs({
  units
}: {
  units: number;
}) {
  const img = 'https://cdn.magicpatterns.com/uploads/ouEiANkRJEreWjgNYUeFWs/foto6.png';
  if (units === 1) {
    return <img src={img} alt="" className="w-[22px] h-auto object-contain" data-id="element-261" />;
  }
  if (units === 2) {
    return <>
        <div className="flex items-center gap-0.5" data-id="element-262">
          <img src={img} alt="" className="w-[16px] h-auto object-contain" data-id="element-263" />
          <img src={img} alt="" className="w-[16px] h-auto object-contain" data-id="element-264" />
        </div>
      </>;
  }
  if (units === 3) {
    return <>
        <div className="w-[36px] h-[28px] relative" data-id="element-265">
          <img src={img} alt="" className="w-[14px] h-auto object-contain absolute top-0 left-1/2 -translate-x-1/2" data-id="element-266" />
          <img src={img} alt="" className="w-[14px] h-auto object-contain absolute bottom-0 left-[1px]" data-id="element-267" />
          <img src={img} alt="" className="w-[14px] h-auto object-contain absolute bottom-0 right-[1px]" data-id="element-268" />
        </div>
      </>;
  }
  return <>
      <div className="w-[36px] h-[28px] relative" data-id="element-269">
        <img src={img} alt="" className="w-[12px] h-auto object-contain absolute top-0 left-[2px]" data-id="element-270" />
        <img src={img} alt="" className="w-[12px] h-auto object-contain absolute top-0 right-[2px]" data-id="element-271" />
        <img src={img} alt="" className="w-[12px] h-auto object-contain absolute bottom-0 left-[2px]" data-id="element-272" />
        <img src={img} alt="" className="w-[12px] h-auto object-contain absolute bottom-0 right-[2px]" data-id="element-273" />
      </div>
    </>;
}
export function PricingSection() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  return <section id="pricing" className="w-full bg-bg-primary py-5 md:py-8 px-4 sm:px-6 lg:px-8" data-id="element-274">
      <div className="max-w-xl mx-auto" data-id="element-275">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-4" data-id="element-276">
          <div className="flex items-center justify-center gap-3" data-id="element-277">
            <div className="h-px bg-border-subtle flex-1" data-id="element-278"></div>
            <p className="text-text-primary font-bold text-[14px] uppercase tracking-wider" data-id="element-279">
              BUY MORE — SAVE MORE
            </p>
            <div className="h-px bg-border-subtle flex-1" data-id="element-280"></div>
          </div>
        </motion.div>

        {/* Bundle Cards — 3-line stacked layout */}
        <div className="space-y-1.5 mb-3" data-id="element-281">
          {bundles.map((b, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.06
        }} className={`relative rounded-lg px-2.5 pt-2 pb-1.5 ${b.highlighted ? 'border-2 border-accent-orange bg-orange-50/30 shadow-[0_0_12px_rgba(249,115,22,0.25)]' : b.topBadge === 'BEST VALUE' ? 'border-2 border-accent-green bg-green-50/20' : 'border border-border-subtle bg-bg-card'}`} data-id="element-282">
              {/* Top-right badge */}
              {b.topBadge && <div className={`absolute -top-2.5 right-3 px-2.5 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider shadow-sm ${b.highlighted ? 'bg-accent-orange text-white' : 'bg-accent-green text-white'}`} data-id="element-283">
                  {b.topBadge}
                </div>}

              {/* LINE 1: Thumbnail + Pack name + SAVE badge */}
              <div className="flex items-center gap-2 mb-1" data-id="element-284">
                <ProductThumbs units={b.units} data-id="element-285" />
                <span className="font-bold text-text-primary text-[14px] leading-tight" data-id="element-286">
                  {b.label}
                </span>
                <span className={`${b.badgeColor} text-[10px] font-bold px-1.5 py-0.5 rounded leading-none`} data-id="element-287">
                  {b.saveBadge}
                </span>
              </div>

              {/* LINE 2: Description + Price */}
              <div className="flex items-end justify-between mb-1.5" data-id="element-288">
                <p className="text-text-secondary text-[11px] leading-tight" data-id="element-289">
                  {b.desc}
                </p>
                <div className="text-right flex-shrink-0 ml-3" data-id="element-290">
                  <span className="text-[10px] text-text-secondary line-through mr-1" data-id="element-291">
                    {b.crossed}
                  </span>
                  <span className="text-[17px] font-bold text-text-primary leading-none" data-id="element-292">
                    {b.price}
                  </span>
                </div>
              </div>

              {/* LINE 3: CTA */}
              <a href={b.checkoutUrl} target="_blank" rel="noopener noreferrer" onClick={() => {
            try {
              const w = window as any;
              if (w.fbq) {
                w.fbq('track', 'AddToCart', {
                  content_name: b.label,
                  content_type: 'product',
                  value: Number(b.price.slice(1)),
                  currency: 'USD'
                });
              }
            } catch (e) {}
          }} className="block w-[75%] mx-auto text-center bg-accent-orange hover:brightness-110 text-white font-bold text-[11px] uppercase tracking-wider py-1.5 rounded-md transition-all duration-300 mt-1" data-id="element-293">
                ADD TO CART
              </a>
            </motion.div>)}
        </div>

        {/* ═══ TRUST STACK ═══ */}

        {/* Payment Banner */}
        <div className="py-2 mb-2" data-id="element-294">
          <img src="https://cdn.magicpatterns.com/uploads/rQtqDPqHt1gp1tHrjZMuXN/baner_pago_seguro.png" alt="Secure payment methods – Visa, Mastercard, PayPal, Apple Pay, Google Pay, Shop Pay, Amex, Klarna" className="w-full h-auto object-contain" data-id="element-295" />
        </div>

        {/* Trust line */}
        <div className="text-center text-[13px] text-text-primary font-semibold mb-4" data-id="element-296">
          <span className="hidden sm:inline" data-id="element-297">
            🔒 Free Shipping · 30-Day Money-Back · 3-Year Warranty · CE & FCC
            Certified
          </span>
          <span className="sm:hidden" data-id="element-298">
            🔒 Free Shipping · 30-Day Money-Back · 3-Year Warranty ·
            <br data-id="element-299" />
            CE & FCC Certified
          </span>
        </div>

        {/* Trust Accordion */}
        <div className="border border-border-subtle rounded-lg overflow-hidden mb-4" data-id="element-300">
          {trustAccordion.map((item, i) => <div key={i} className={i < trustAccordion.length - 1 ? 'border-b border-border-subtle' : ''} data-id="element-301">
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-4 py-3 text-left focus:outline-none" data-id="element-302">
                <span className="text-text-primary font-medium text-[14px]" data-id="element-303">
                  {item.title}
                </span>
                <ChevronDown className={`w-4 h-4 text-text-secondary flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} data-id="element-304" />
              </button>
              <AnimatePresence data-id="element-305">
                {openFaq === i && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.25,
              ease: 'easeInOut'
            }} data-id="element-306">
                    <div className="px-4 pb-3 text-text-secondary text-[13px] leading-[1.5]" data-id="element-307">
                      {item.content}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>

        {/* Review Card */}
        <div className="bg-bg-card border border-border-subtle rounded-xl p-4 shadow-card" data-id="element-308">
          <div className="flex items-center gap-3 mb-2.5" data-id="element-309">
            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-[13px] flex-shrink-0" data-id="element-310">
              LJ
            </div>
            <div className="flex-1" data-id="element-311">
              <div className="flex items-center gap-1.5" data-id="element-312">
                <span className="font-bold text-text-primary text-[14px]" data-id="element-313">
                  Lauren J.
                </span>
                <CheckCircle2 className="w-4 h-4 text-blue-500" data-id="element-314" />
              </div>
            </div>
          </div>
          <div className="flex items-center text-star-gold mb-2" data-id="element-315">
            {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" data-id="element-316" />)}
          </div>
          <p className="text-text-secondary text-[14px] leading-[1.5]" data-id="element-317">
            "Our HVAC guy checked our 8-year-old detector. Still green,
            basically useless. Replaced it with AirGuard and now we can see the
            actual number. Finally know our kids are safe."
          </p>
        </div>
      </div>
    </section>;
}